import React, { useState } from 'react'
import styled from '@emotion/styled'
import { Radio } from 'antd'
import type { RadioChangeEvent } from 'antd'
import { Section } from 'components/Common'
import { StepStatus } from 'hooks/useStep'
import { ShipdayIntegrationType } from './types'
import { ActionCard, OutlineButton, PrimaryButton } from './styles'
import StepLayout from './StepLayout'

interface IntegrationTypeStepProps {
  stepNumber: number
  loading?: boolean
  onContinue: (type: ShipdayIntegrationType) => void
  onBack: () => void
}

const IntegrationTypeStep = ({
  stepNumber,
  loading,
  onContinue,
  onBack
}: IntegrationTypeStepProps) => {
  const [selectedType, setSelectedType] =
    useState<ShipdayIntegrationType>('location')

  const handleChange = (e: RadioChangeEvent) => {
    setSelectedType(e.target.value)
  }

  return (
    <StepLayout>
      <Section
        title='Choose your integration type'
        description='Select whether this is a merchant-level or store-level account'
        stepStatus={StepStatus.active}
        stepNumber={stepNumber}
      >
        <Radio.Group
          value={selectedType}
          onChange={handleChange}
          style={{ flexDirection: 'column', gap: '12px' }}
        >
          <RadioLabel active={selectedType === 'location'}>
            <Radio value='location' />
            <div>
              <RadioTitle>LOCATION-LEVEL ACCOUNT</RadioTitle>
              <RadioDescription>
                Choose which stores to connect to this Shipday account. All
                selected stores will share the same Shipday account.
              </RadioDescription>
            </div>
          </RadioLabel>

          <RadioLabel active={selectedType === 'merchant'}>
            <Radio value='merchant' />
            <div>
              <RadioTitle>MERCHANT-LEVEL ACCOUNT</RadioTitle>
              <RadioDescription>
                All your stores share the same Shipday integration. Best if you
                manage all deliveries centrally.
              </RadioDescription>
            </div>
          </RadioLabel>
        </Radio.Group>
      </Section>
      <ActionCard>
        <OutlineButton style={{ width: 'auto' }} onClick={onBack}>
          BACK
        </OutlineButton>
        <PrimaryButton
          disabled={loading}
          loading={loading}
          onClick={() => onContinue(selectedType)}
        >
          CONTINUE
        </PrimaryButton>
      </ActionCard>
    </StepLayout>
  )
}

const RadioLabel = styled.label<{ active: boolean }>(
  ({ active }) => `
  padding: 8px 12px;
  border: 1px solid ${active ? '#4a4a68' : '#d2d2d9'};
  border-radius: 6px;
  background: ${active ? '#fafafb' : '#ffffff'};

  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
`
)

const RadioTitle = styled.p`
  font-size: 14px;
  font-weight: 600;
  color: #4a4a68;
  margin-bottom: 0;
  text-align: left;
`

const RadioDescription = styled.p`
  font-size: 12px;
  font-weight: 400;
  color: #77778e;
  margin-bottom: 0;
  text-align: left;
`

export default IntegrationTypeStep
